import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SceneSystem } from './SceneSystem';
import { MODEL_SCALES, MODEL_SCALE_OVERRIDES } from '../core/constants';
import { applyTronMaterial } from '../core/tron-material';
import type { ModelPlacement } from '../core/types';
import { assetUrl } from '../../lib/assets';

interface LabelEntry {
  el: HTMLDivElement;
  coordinates: [number, number];
}

export class ModelSystem {
  private scene: SceneSystem;
  private loader = new GLTFLoader();
  private loadedModels = new Map<string, THREE.Group>();
  private meshes: THREE.Object3D[] = [];
  private labels: LabelEntry[] = [];
  private raycaster = new THREE.Raycaster();
  private onPanelClick: (panel: string) => void;
  private labelUpdateId = 0;

  constructor(scene: SceneSystem, onPanelClick: (panel: string) => void) {
    this.scene = scene;
    this.onPanelClick = onPanelClick;
  }

  async loadAll(placements: ModelPlacement[]): Promise<void> {
    const uniqueModels = [...new Set(placements.map(p => p.modelId))];
    await Promise.all(uniqueModels.map(id => this.loadModel(id)));

    for (const placement of placements) {
      this.placeModel(placement);
    }

    this.startLabelUpdates();
  }

  private startLabelUpdates(): void {
    const update = () => {
      if (!this.scene.map) return;
      for (const label of this.labels) {
        const point = this.scene.map.project(label.coordinates);
        label.el.style.left = point.x + 'px';
        label.el.style.top = (point.y - 20) + 'px';
      }
      this.labelUpdateId = requestAnimationFrame(update);
    };
    this.labelUpdateId = requestAnimationFrame(update);
  }

  setModelEmissive(coords: [number, number], color: THREE.Color, intensity: number): void {
    const mesh = this.getModelAtCoordinates(coords);
    if (!mesh) return;
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material?.isMeshStandardMaterial) {
        (child.material as THREE.MeshStandardMaterial).emissive = color;
        (child.material as THREE.MeshStandardMaterial).emissiveIntensity = intensity;
      }
    });
  }

  resetModelEmissive(coords: [number, number]): void {
    this.setModelEmissive(coords, new THREE.Color(0x000000), 0);
  }

  handleClick(event: MouseEvent, canvas: HTMLCanvasElement): string | null {
    const rect = canvas.getBoundingClientRect();
    const ndcX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const near = new THREE.Vector3(ndcX, ndcY, -1).applyMatrix4(this.scene.camera.projectionMatrixInverse);
    const far = new THREE.Vector3(ndcX, ndcY, 1).applyMatrix4(this.scene.camera.projectionMatrixInverse);
    this.raycaster.set(near, far.sub(near).normalize());

    const intersects = this.raycaster.intersectObjects(this.meshes, true);
    if (intersects.length > 0) {
      let obj: THREE.Object3D | null = intersects[0].object;
      while (obj && !obj.userData?.panel) obj = obj.parent;
      if (obj?.userData?.panel) {
        this.onPanelClick(obj.userData.panel);
        return obj.userData.panel;
      }
    }
    return null;
  }

  // Editor accessors
  getMeshCount(): number { return this.meshes.length; }

  getMeshWorldPosition(index: number): THREE.Vector3 | null {
    return this.meshes[index]?.position.clone() ?? null;
  }

  getMeshData(index: number) {
    const mesh = this.meshes[index];
    if (!mesh) return null;
    return {
      coordinates: this.scene.worldToLngLat(mesh.position),
      rotation: (mesh.rotation.y * 180) / Math.PI,
      scale: mesh.scale.x,
      label: mesh.userData.label || '',
      modelId: mesh.userData.modelId || '',
      type: mesh.userData.type || '',
    };
  }

  setMeshPosition(index: number, pos: THREE.Vector3): void {
    const mesh = this.meshes[index];
    if (mesh) mesh.position.copy(pos);
  }

  setMeshRotation(index: number, degrees: number): void {
    const mesh = this.meshes[index];
    if (mesh) mesh.rotation.y = (degrees * Math.PI) / 180;
  }

  setMeshScale(index: number, scale: number): void {
    const mesh = this.meshes[index];
    if (mesh) mesh.scale.setScalar(scale);
  }

  raycastIndex(raycaster: THREE.Raycaster): number | null {
    const intersects = raycaster.intersectObjects(this.meshes, true);
    if (intersects.length > 0) {
      let obj: THREE.Object3D | null = intersects[0].object;
      while (obj && !this.meshes.includes(obj)) obj = obj.parent;
      if (obj) return this.meshes.indexOf(obj);
    }
    return null;
  }

  dispose(): void {
    cancelAnimationFrame(this.labelUpdateId);
    for (const label of this.labels) label.el.remove();
    this.labels = [];
    for (const mesh of this.meshes) {
      this.scene.remove(mesh);
      mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose();
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material?.dispose();
        }
      });
    }
    this.meshes = [];
    this.loadedModels.clear();
  }

  private async loadModel(modelId: string): Promise<void> {
    if (this.loadedModels.has(modelId)) return;
    try {
      const gltf = await this.loader.loadAsync(assetUrl(`/models/${modelId}.glb`));
      gltf.scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      this.loadedModels.set(modelId, gltf.scene);
    } catch (e) {
      console.warn(`[ModelSystem] Failed to load ${modelId}:`, e);
    }
  }

  private placeModel(placement: ModelPlacement): void {
    const template = this.loadedModels.get(placement.modelId);
    if (!template) return;

    const mesh = template.clone();
    const pos = this.scene.lngLatToWorld(placement.coordinates[0], placement.coordinates[1]);
    mesh.position.copy(pos);
    const scale = MODEL_SCALE_OVERRIDES[placement.modelId] ?? MODEL_SCALES[placement.type] ?? 5;
    mesh.scale.setScalar(scale);
    mesh.rotation.y = (placement.rotation * Math.PI) / 180;
    mesh.userData = {
      label: placement.label,
      type: placement.type,
      modelId: placement.modelId,
      panel: placement.panel,
      coordinates: placement.coordinates,
    };

    applyTronMaterial(mesh, placement.type);

    this.scene.add(mesh);
    this.meshes.push(mesh);

    if (placement.label && this.scene.map) {
      const labelEl = document.createElement('div');
      labelEl.textContent = placement.label;
      labelEl.style.cssText = 'position:absolute;pointer-events:none;font-size:9px;font-family:Inter,sans-serif;font-weight:500;color:#64748b;text-shadow:0 0 3px #000;white-space:nowrap;transform:translate(-50%,-100%);';
      this.scene.map.getCanvasContainer().appendChild(labelEl);
      this.labels.push({ el: labelEl, coordinates: placement.coordinates });
    }
  }

  private getModelAtCoordinates(coords: [number, number]): THREE.Object3D | null {
    for (const mesh of this.meshes) {
      if (mesh.userData.coordinates &&
          Math.abs(mesh.userData.coordinates[0] - coords[0]) < 0.0005 &&
          Math.abs(mesh.userData.coordinates[1] - coords[1]) < 0.0005) {
        return mesh;
      }
    }
    return null;
  }
}
